/**
 * Help & Docs page (ADR-0069, spec §6.3).
 *
 * Fetches the section index then all section content in parallel.
 * Renders a two-column layout: sticky TOC (left) + scrollable body (right).
 * Narrow viewports (<= 900px) replace the TOC with a <select> jump menu.
 * "Chat with Ask Atlas" button dispatches atlas:open-help-widget.
 */

import { useEffect, useRef, useState } from "react";
import { marked } from "marked";
import { apiFetch } from "../lib/api.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { PageLayout } from "../components/PageLayout.js";

type SectionIndex = { id: string; title: string; summary: string };
type SectionDetail = { id: string; title: string; content: string };

function parseMarkdown(md: string): string {
  const result = marked.parse(md);
  return typeof result === "string" ? result : "";
}

export function HelpDocs() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sections, setSections] = useState<SectionDetail[]>([]);
  const [index, setIndex] = useState<SectionIndex[]>([]);
  const [narrow, setNarrow] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    setNarrow(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const { sections: idx } = await apiFetch<{ sections: SectionIndex[] }>("/api/tenant/help/index");
        setIndex(idx);
        const details = await Promise.all(
          idx.map((s) => apiFetch<SectionDetail>(`/api/tenant/help/sections/${s.id}`))
        );
        setSections(details);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function scrollTo(id: string) {
    const el = bodyRef.current?.querySelector(`#section-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function openChat() {
    document.dispatchEvent(new CustomEvent("atlas:open-help-widget"));
  }

  if (error) return <PageLayout title="Help &amp; Docs"><ErrorState detail={error} /></PageLayout>;
  if (loading) return <PageLayout title="Help &amp; Docs"><LoadingState /></PageLayout>;

  return (
    <PageLayout title="Help &amp; Docs">
      <div style={{ marginBottom: 12 }}>
        <button type="button" className="btn btnPrimary" onClick={openChat}>
          Chat with Ask Atlas
        </button>
      </div>

      <div className="helpDocsLayout">
        {/* TOC */}
        {!narrow ? (
          <nav className="helpDocsToc" aria-label="Table of contents">
            {index.map((s) => (
              <a
                key={s.id}
                href={`#section-${s.id}`}
                className="helpDocsTocLink"
                onClick={(e) => { e.preventDefault(); scrollTo(s.id); }}
              >
                {s.title}
              </a>
            ))}
          </nav>
        ) : (
          <div style={{ marginBottom: 12 }}>
            <select
              className="select"
              aria-label="Jump to section"
              onChange={(e) => scrollTo(e.target.value)}
              defaultValue=""
            >
              <option value="" disabled>Jump to section…</option>
              {index.map((s) => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
          </div>
        )}

        {/* Body */}
        <div className="helpDocsBody" ref={bodyRef}>
          {sections.map((s) => (
            <div key={s.id} id={`section-${s.id}`} style={{ marginBottom: 40 }}>
              <div
                className="helpDocsContent"
                dangerouslySetInnerHTML={{ __html: parseMarkdown(s.content) }}
              />
            </div>
          ))}
        </div>
      </div>
    </PageLayout>
  );
}
